<template>
  <div class="loyalty-store-campaign-form">
    <div class="category-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="backButton">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
        <div class="text-right">
          <p class="d-inline-flex">
            <a class="btn btn-danger btn-icon delete mar-rt-10" v-if="campaignId" @click.prevent="handleDelete">
              <img src="/static/img/icons/icon-close.png">
              {{ $t('general.delete') }}
            </a>
            <button type="button" @click.prevent="save" :disabled="loading || rewardSaving" class="btn btn-icon btn-success mar-rt-10 save">
              <img src="/static/img/icons/save.png">
              {{ $t('general.save') }}
            </button>
          </p>
        </div>
      </div>
    </div>

    <div :class="(loading ? 'show-loader' : 'hide-loader')">
      <div class="el-loading-spinner">
        <svg viewBox="25 25 50 50" class="circular">
          <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
        </svg>
      </div>
    </div>

    <div class="card mar-to-15">
      <div class="card-content">
        <h5>{{ $t('loyaltyStore.generalSettings') }}</h5>
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('loyaltyStore.campaignName') }}</label>
              <input v-model="model.name" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>{{ $t('loyaltyStore.description') }}</label>
              <textarea v-model="model.description" rows="3" class="form-control"></textarea>
            </div>
            <div class="form-group">
              <p-switch v-model="model.active" type="success"></p-switch>
              <label class="mar-lt-10">{{ $t('loyaltyStore.active') }}</label>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('loyaltyStore.availability') }}</label>
              <el-date-picker
                v-model="availabilityRange"
                type="daterange"
                value-format="yyyy-MM-dd"
                format="dd/MM/yyyy"
                style="width:100%"
              />
            </div>
            <div class="form-group">
              <label>{{ $t('loyaltyStore.locations') }}</label>
              <v-select
                v-model="locationsFormatted"
                multiple
                :options="locations"
                :placeholder="$t('general.all')"
                label="name"
              />
              <small><i class="fa fa-info-circle"></i> {{ $t('loyaltyStore.locationsInfo') }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card mar-to-15">
      <div class="card-content">
        <div class="display-flex" style="justify-content: space-between; align-items: center;">
          <h5 class="mar-bt-0">{{ $t('loyaltyStore.rewards') }}</h5>
          <button type="button" class="btn btn-info btn-sm" :disabled="loading || rewardSaving" @click="openRewardForm()">
            {{ $t('loyaltyStore.addReward') }}
          </button>
        </div>

        <el-table
          ref="rewardsTable"
          class="loyalty-store-rewards-table"
          :data="rewards"
          :empty-text="$t('gestiune.noData')"
          style="width: 100%; margin-top: 15px;"
        >
          <el-table-column
            class-name="centered-cell"
            align="center"
            header-align="center"
            prop="id"
            :label="$t('customers.id')"
            :min-width="70"
          >
            <template slot-scope="props">{{ props.row.id || '—' }}</template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            align="center"
            header-align="center"
            :label="$t('loyaltyStore.reward')"
            :min-width="180"
          >
            <template slot-scope="props">
              {{ props.row.name || props.row.reward_name || (props.row.product && props.row.product.name) || ('#' + props.row.id) }}
            </template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            align="center"
            header-align="center"
            :label="$t('loyaltyStore.rewardType')"
            :min-width="110"
          >
            <template slot-scope="props">
              {{ rewardTypeLabel(props.row) }}
            </template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            align="center"
            header-align="center"
            :label="$t('loyaltyStore.pointsCost')"
            :min-width="150"
          >
            <template slot-scope="props">
              {{ props.row.loyalty_points_cost }}
            </template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            align="center"
            header-align="center"
            :label="$t('loyaltyStore.giftCardValue')"
            :min-width="140"
          >
            <template slot-scope="props">
              <span v-if="isGiftReward(props.row)">{{ props.row.gift_card_value }}</span>
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            align="center"
            header-align="center"
            :label="$t('loyaltyStore.active')"
            :min-width="90"
          >
            <template slot-scope="props">
              {{ props.row.active ? $t('general.yes') : $t('general.no') }}
            </template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            align="center"
            header-align="center"
            :label="$t('marketingCampaigns.actions')"
            :width="100"
          >
            <template slot-scope="props">
              <el-dropdown trigger="click" placement="bottom-end" @click.native.stop>
                <span class="arrow-box">&#9662;</span>
                <el-dropdown-menu slot="dropdown" class="custom-dropdown-menu">
                  <el-dropdown-item @click.native.stop="openRewardForm(props.row)">
                    {{ $t('general.edit') }}
                  </el-dropdown-item>
                  <el-dropdown-item @click.native.stop="deleteReward(props.row)">
                    {{ $t('general.delete') }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
    <el-dialog
      :title="editingReward ? $t('loyaltyStore.editReward') : $t('loyaltyStore.addReward')"
      :visible.sync="rewardDialogVisible"
      width="520px"
      append-to-body
      :modal-append-to-body="true"
      :z-index="100000"
      custom-class="loyalty-store-reward-dialog"
    >
      <div class="form-group">
        <label>{{ $t('loyaltyStore.rewardType') }}</label>
        <select v-model="rewardForm.type" class="form-control" :disabled="!!(editingReward && editingReward.id)">
          <option :value="1">{{ $t('loyaltyStore.typeProduct') }}</option>
          <option :value="2" :disabled="!giftCardsEnabled">{{ $t('loyaltyStore.typeGiftCard') }}</option>
        </select>
        <small v-if="!giftCardsEnabled" class="text-muted">{{ $t('loyaltyStore.giftCardsModuleRequired') }}</small>
      </div>
      <div class="form-group" v-if="rewardForm.type === 1">
        <label>{{ $t('loyaltyStore.category') }}</label>
        <el-select
          v-model="rewardForm.category_id"
          filterable
          remote
          clearable
          :remote-method="searchCategories"
          :loading="categoriesLoading"
          style="width: 100%;"
          :placeholder="$t('loyaltyStore.searchCategory')"
          @visible-change="onCategoryDropdown"
          @change="onCategoryChange"
        >
          <el-option
            v-for="c in categoryOptions"
            :key="c.id"
            :label="c.name || c.label"
            :value="c.id"
          />
        </el-select>
      </div>
      <div class="form-group" v-if="rewardForm.type === 1">
        <label>{{ $t('loyaltyStore.product') }}</label>
        <el-select
          v-model="rewardForm.catalogproduct_id"
          filterable
          remote
          clearable
          :remote-method="searchProducts"
          :loading="productsLoading"
          style="width: 100%;"
          :placeholder="$t('loyaltyStore.searchProduct')"
          @visible-change="onProductDropdown"
        >
          <el-option
            v-for="p in productOptions"
            :key="p.id"
            :label="p.name + (p.price != null ? ' (' + p.price + ')' : '')"
            :value="p.id"
          />
        </el-select>
      </div>
      <div class="form-group" v-if="rewardForm.type === 2">
        <label>{{ $t('loyaltyStore.giftCardValue') }}</label>
        <input v-model.number="rewardForm.gift_card_value" type="number" min="0.01" step="0.01" class="form-control" />
      </div>
      <div class="form-group">
        <label>{{ $t('loyaltyStore.pointsCost') }}</label>
        <input v-model.number="rewardForm.loyalty_points_cost" type="number" min="1" step="1" class="form-control" />
      </div>
      <div class="form-group">
        <p-switch v-model="rewardForm.active" type="success"></p-switch>
        <label class="mar-lt-10">{{ $t('loyaltyStore.active') }}</label>
      </div>
      <span slot="footer" class="loyalty-store-reward-dialog__footer">
        <button class="btn btn-default" type="button" @click="rewardDialogVisible = false">{{ $t('general.cancel') || 'Cancel' }}</button>
        <button class="btn btn-fill btn-info" type="button" :disabled="rewardSaving" @click="saveReward">{{ $t('general.save') }}</button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import swal from 'sweetalert2'
import { DatePicker, Select, Option, Dialog, Table, TableColumn, Dropdown, DropdownMenu, DropdownItem } from 'element-ui'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import vSelect from 'vue-select'

Vue.use(DatePicker)
Vue.use(Select)
Vue.use(Option)
Vue.use(Dialog)
Vue.use(Table)
Vue.use(TableColumn)
Vue.use(Dropdown)
Vue.use(DropdownMenu)
Vue.use(DropdownItem)

export default {
  components: {
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    PSwitch,
    vSelect
  },
  props: {
    route: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: false,
      campaignId: null,
      availabilityRange: [],
      locations: [],
      locationsFormatted: [],
      rewards: [],
      nextDraftRewardId: 1,
      rewardSaving: false,
      giftCardModuleEnabled: null,
      model: {
        name: '',
        description: '',
        active: true
      },
      rewardDialogVisible: false,
      editingReward: null,
      rewardForm: {
        type: 1,
        category_id: null,
        catalogproduct_id: null,
        gift_card_value: null,
        loyalty_points_cost: 1,
        active: true
      },
      categoryOptions: [],
      categoriesLoading: false,
      productOptions: [],
      productsLoading: false
    }
  },
  computed: {
    giftCardsEnabled () {
      const company = this.$session.get('selectedCompanyData') || {}
      const value = this.giftCardModuleEnabled === null ? company.use_marketing_gift_card : this.giftCardModuleEnabled
      return value === true || value === 1 || value === '1' || value === 'true'
    }
  },
  created () {
    const fromProp = this.route && this.route.params && this.route.params.id
    const fromRouter = this.$route && this.$route.params && this.$route.params.id
    this.campaignId = fromProp || fromRouter || null
    this.loadLocations()
    this.loadCompanyFeatures()
    if (this.campaignId) {
      this.loadCampaign()
    }
  },
  methods: {
    loadCompanyFeatures () {
      const companyId = this.$session.get('selectedCompany')
      this.apiRequest('/settings/general/' + companyId, 'GET', { token: this.$session.get('token') }, (response) => {
        const company = response.status && response.data && response.data[0]
        if (!company || company.use_marketing_gift_card === undefined) return
        this.giftCardModuleEnabled = company.use_marketing_gift_card
        const session = this.$session.get('selectedCompanyData') || {}
        this.$session.set('selectedCompanyData', Object.assign({}, session, { use_marketing_gift_card: company.use_marketing_gift_card }))
      })
    },
    isGiftReward (row) {
      return Number(row.type) === 2 || row.type_label === 'gift_card'
    },
    rewardTypeLabel (row) {
      return this.isGiftReward(row)
        ? this.$t('loyaltyStore.typeGiftCard')
        : this.$t('loyaltyStore.typeProduct')
    },
    backButton () {
      this.$bus.$emit('close-modal')
    },
    loadLocations () {
      this.setLocationsFromSession()
    },
    setLocationsFromSession () {
      const company = this.$session.get('selectedCompanyData') || {}
      const locs = company.locations_details || []
      this.locations = locs.map((l) => ({
        id: l.true_id || l.id,
        name: l.name
      }))
    },
    loadCampaign () {
      this.loading = true
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/loyalty-store-campaigns/' + this.campaignId, 'GET', data, (response) => {
        this.loading = false
        if (!response.status || !response.data) {
          swal('', this.$t('validations.wentWrong'), 'error')
          return
        }
        const item = response.data
        this.model.name = item.name || ''
        this.model.description = item.description || ''
        this.model.active = !!item.active
        this.availabilityRange = []
        if (item.starts_at || item.ends_at) {
          const from = item.starts_at ? String(item.starts_at).substring(0, 10) : null
          const to = item.ends_at ? String(item.ends_at).substring(0, 10) : null
          if (from || to) this.availabilityRange = [from || to, to || from]
        }
        const ids = item.location_ids || []
        this.locationsFormatted = this.locations.filter((loc) => ids.map(Number).includes(Number(loc.id)))
        this.rewards = item.rewards || []
        this.$nextTick(() => this.layoutRewardsTable())
      })
    },
    layoutRewardsTable () {
      const table = this.$refs.rewardsTable
      if (table && typeof table.doLayout === 'function') {
        table.doLayout()
      }
    },
    payload () {
      const range = this.availabilityRange || []
      return {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        name: this.model.name,
        description: this.model.description,
        active: this.model.active ? 1 : 0,
        starts_at: range[0] || null,
        ends_at: range[1] || null,
        location_ids: (this.locationsFormatted || []).map((l) => l.id)
      }
    },
    save () {
      if (this.loading || this.rewardSaving) return
      if (!this.model.name || !String(this.model.name).trim()) {
        swal('', this.$t('loyaltyStore.nameRequired'), 'error')
        return
      }
      this.loading = true
      const body = this.payload()
      const done = (response) => {
        if (!response.status) {
          this.loading = false
          swal('', this.$t('validations.wentWrong'), 'error')
          return
        }
        if (!this.campaignId) {
          if (!response.data || !response.data.id) {
            this.loading = false
            swal('', this.$t('validations.wentWrong'), 'error')
            return
          }
          this.campaignId = response.data.id
        }
        this.saveDraftRewards(() => {
          this.loading = false
          swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
          this.$bus.$emit('refresh')
          this.loadCampaign()
        })
      }
      if (this.campaignId) {
        this.apiRequest('/loyalty-store-campaigns/' + this.campaignId, 'PUT', body, done)
      } else {
        this.apiRequest('/loyalty-store-campaigns', 'POST', body, done)
      }
    },
    rewardPayload (reward) {
      return {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        type: reward.type,
        catalogproduct_id: reward.type === 1 ? reward.catalogproduct_id : null,
        gift_card_value: reward.type === 2 ? reward.gift_card_value : null,
        loyalty_points_cost: reward.loyalty_points_cost,
        active: reward.active ? 1 : 0
      }
    },
    saveDraftRewards (done) {
      const index = this.rewards.findIndex((reward) => reward._draftId)
      if (index === -1) {
        done()
        return
      }
      const reward = this.rewards[index]
      this.apiRequest('/loyalty-store-campaigns/' + this.campaignId + '/rewards', 'POST', this.rewardPayload(reward), (response) => {
        if (!response.status || !response.data || !response.data.id) {
          this.loading = false
          this.$bus.$emit('refresh')
          swal('', this.$t('loyaltyStore.rewardsSaveFailed'), 'error')
          return
        }
        // Replace each draft as it succeeds so retrying never resubmits saved rewards.
        this.rewards.splice(index, 1, response.data)
        this.saveDraftRewards(done)
      })
    },
    handleDelete () {
      if (this.loading || this.rewardSaving) return
      swal({
        title: this.$t('loyaltyStore.deleteConfirmation'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.no')
      }).then((result) => {
        if (result !== true && !(result && result.value === true)) return
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/loyalty-store-campaigns/' + this.campaignId, 'DELETE', data, (response) => {
          if (response.status) {
            this.$bus.$emit('refresh')
            this.$bus.$emit('close-modal')
          } else {
            swal('', this.$t('validations.wentWrong'), 'error')
          }
        })
      }).catch(() => {})
    },
    openRewardForm (row) {
      if (this.loading || this.rewardSaving) return
      this.editingReward = row || null
      if (row) {
        this.rewardForm = {
          type: Number(row.type) === 2 ? 2 : 1,
          category_id: (row.product && row.product.category_id) || row.category_id || null,
          catalogproduct_id: row.catalogproduct_id || null,
          gift_card_value: row.gift_card_value,
          loyalty_points_cost: row.loyalty_points_cost,
          active: !!row.active
        }
        if (row.product) {
          this.productOptions = [row.product]
          if (row.product.category_id) {
            this.categoryOptions = [{
              id: row.product.category_id,
              name: row.product.category_name || ('#' + row.product.category_id)
            }]
          }
        }
      } else {
        this.rewardForm = {
          type: 1,
          category_id: null,
          catalogproduct_id: null,
          gift_card_value: null,
          loyalty_points_cost: 1,
          active: true
        }
        this.productOptions = []
        this.categoryOptions = []
      }
      this.rewardDialogVisible = true
      this.searchCategories('')
      this.searchProducts('')
    },
    loyaltyPickerBaseUrl () {
      return this.campaignId
        ? '/loyalty-store-campaigns/' + this.campaignId
        : '/loyalty-store-campaigns'
    },
    pickerLocationIds () {
      return (this.locationsFormatted || []).map((l) => l.id).filter((id) => id != null && id !== '')
    },
    appendPickerLocationParams (data) {
      const ids = this.pickerLocationIds()
      // Always send so API prefers form selection over stale campaign locations.
      data.location_ids = ids
      return data
    },
    onCategoryDropdown (visible) {
      if (visible && !this.categoryOptions.length) this.searchCategories('')
    },
    onProductDropdown (visible) {
      if (visible && !this.productOptions.length) this.searchProducts('')
    },
    onCategoryChange () {
      this.rewardForm.catalogproduct_id = null
      this.productOptions = []
      this.searchProducts('')
    },
    searchCategories (query) {
      this.categoriesLoading = true
      const data = this.appendPickerLocationParams({
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        search: query || '',
        per_page: 20
      })
      this.apiRequest(this.loyaltyPickerBaseUrl() + '/available-categories', 'GET', data, (response) => {
        this.categoriesLoading = false
        if (!response.status) return
        const d = response.data
        let options = Array.isArray(d) ? d : (d && d.data ? d.data : [])
        const selectedId = this.rewardForm.category_id
        if (selectedId && !options.some((c) => Number(c.id) === Number(selectedId))) {
          const existing = this.categoryOptions.find((c) => Number(c.id) === Number(selectedId))
          if (existing) options = [existing].concat(options)
        }
        this.categoryOptions = options
      })
    },
    searchProducts (query) {
      this.productsLoading = true
      const data = this.appendPickerLocationParams({
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        search: query || '',
        per_page: 20
      })
      if (this.rewardForm.category_id) {
        data.category_id = this.rewardForm.category_id
      }
      this.apiRequest(this.loyaltyPickerBaseUrl() + '/available-products', 'GET', data, (response) => {
        this.productsLoading = false
        if (!response.status) return
        const d = response.data
        let options = Array.isArray(d) ? d : (d && d.data ? d.data : [])
        const selectedId = this.rewardForm.catalogproduct_id
        if (selectedId && !options.some((p) => Number(p.id) === Number(selectedId))) {
          const existing = this.productOptions.find((p) => Number(p.id) === Number(selectedId))
          if (existing) options = [existing].concat(options)
        }
        this.productOptions = options
      })
    },
    saveReward () {
      if (this.loading || this.rewardSaving) return
      if (!Number.isInteger(this.rewardForm.loyalty_points_cost) || this.rewardForm.loyalty_points_cost < 1) {
        swal('', this.$t('loyaltyStore.pointsCostRequired'), 'error')
        return
      }
      if (this.rewardForm.type === 1 && !this.rewardForm.catalogproduct_id) {
        swal('', this.$t('loyaltyStore.productRequired'), 'error')
        return
      }
      if (this.rewardForm.type === 2 && (!Number.isFinite(this.rewardForm.gift_card_value) || this.rewardForm.gift_card_value <= 0)) {
        swal('', this.$t('loyaltyStore.giftValueRequired'), 'error')
        return
      }
      if (this.rewardForm.type === 2 && !this.giftCardsEnabled) {
        swal('', this.$t('loyaltyStore.giftCardsModuleRequired'), 'error')
        return
      }
      const body = this.rewardPayload(this.rewardForm)
      if (!this.campaignId || (this.editingReward && this.editingReward._draftId)) {
        const product = this.rewardForm.type === 1
          ? this.productOptions.find((p) => Number(p.id) === Number(this.rewardForm.catalogproduct_id))
          : null
        const draft = Object.assign({}, this.rewardForm, {
          _draftId: this.editingReward ? this.editingReward._draftId : this.nextDraftRewardId++,
          product,
          name: product ? product.name : this.$t('loyaltyStore.typeGiftCard')
        })
        const index = this.rewards.indexOf(this.editingReward)
        if (index === -1) this.rewards.push(draft)
        else this.rewards.splice(index, 1, draft)
        this.rewardDialogVisible = false
        this.$nextTick(() => this.layoutRewardsTable())
        return
      }
      this.rewardSaving = true
      const done = (response) => {
        this.rewardSaving = false
        if (!response.status) {
          const msg = (response.errors && (response.errors.type || response.errors.catalogproduct_id || response.errors.gift_card_value)) || this.$t('validations.wentWrong')
          swal('', Array.isArray(msg) ? msg[0] : msg, 'error')
          return
        }
        this.rewardDialogVisible = false
        const index = this.rewards.indexOf(this.editingReward)
        if (index === -1) this.rewards.push(response.data)
        else this.rewards.splice(index, 1, response.data)
        this.$nextTick(() => this.layoutRewardsTable())
      }
      if (this.editingReward && this.editingReward.id) {
        this.apiRequest('/loyalty-store-rewards/' + this.editingReward.id, 'PUT', body, done)
      } else {
        this.apiRequest('/loyalty-store-campaigns/' + this.campaignId + '/rewards', 'POST', body, done)
      }
    },
    deleteReward (row) {
      if (this.loading || this.rewardSaving) return
      swal({
        title: this.$t('loyaltyStore.deleteConfirmation'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.no')
      }).then((result) => {
        if (result !== true && !(result && result.value === true)) return
        if (row._draftId) {
          this.rewards.splice(this.rewards.indexOf(row), 1)
          return
        }
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/loyalty-store-rewards/' + row.id, 'DELETE', data, (response) => {
          if (response.status) this.rewards = this.rewards.filter((reward) => reward !== row)
          else swal('', this.$t('validations.wentWrong'), 'error')
        })
      }).catch(() => {})
    }
  }
}
</script>

<style>
.loyalty-store-campaign-form .loyalty-store-rewards-table .centered-cell .cell {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
  white-space: normal;
  line-height: 1.3;
  word-break: break-word;
}

.loyalty-store-campaign-form .loyalty-store-rewards-table .el-table__header th .cell {
  text-align: center !important;
  white-space: normal;
  line-height: 1.3;
  word-break: break-word;
}

.loyalty-store-campaign-form .arrow-box {
  display: inline-block;
  font-size: 18px;
  padding: 4px 6px;
  border: 1px solid #674BD2 !important;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  user-select: none;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
  color: #674BD2;
}

.loyalty-store-campaign-form .arrow-box:hover {
  background-color: #674BD2 !important;
  border-color: #674BD2 !important;
  color: #fff !important;
}

.loyalty-store-campaign-form .custom-dropdown-menu {
  min-width: 160px;
  font-size: 14px;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/LoyaltyStore/LoyaltyStoreCampaignForm.vue